import { Link } from 'react-router-dom';
import type { Topic } from '@/mocks/topics';
import { formatCount, statusStyle } from '@/utils/format';

interface TopicCardProps {
  topic: Topic;
  index?: number;
}

export default function TopicCard({ topic, index }: TopicCardProps) {
  const style = statusStyle(topic.status);

  return (
    <article className="group flex h-full flex-col overflow-hidden border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/topic/${topic.id}`} className="block">
        <div className="relative h-44 w-full overflow-hidden bg-background-200 md:h-48">
          <img
            src={topic.cover}
            alt={`${topic.title} 话题配图`}
            title={`${topic.title} - ${topic.categoryName}`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute left-3 top-3 flex items-center gap-1.5 bg-background-50/95 px-2.5 py-1 text-xs text-foreground-800">
            <i className="ri-price-tag-3-line text-primary-500" aria-hidden="true" />
            {topic.categoryName}
          </span>
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-5">
        <div className="mb-3 flex items-center gap-2">
          <span className={`flex items-center gap-1.5 px-2 py-0.5 text-xs ${style.badge}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} aria-hidden="true" />
            {topic.status}
          </span>
          {typeof index === 'number' && (
            <span className="font-heading text-xs text-foreground-400">
              热榜 No.{index + 1}
            </span>
          )}
        </div>

        <h3 className="font-heading text-base font-bold leading-snug text-foreground-950">
          <Link to={`/topic/${topic.id}`} className="transition-colors hover:text-primary-600">
            {topic.title}
          </Link>
        </h3>

        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{topic.summary}</p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {topic.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="bg-background-100 px-2 py-0.5 text-xs text-foreground-600">
              #{tag}
            </span>
          ))}
        </div>

        <div className="mt-auto flex items-center justify-between border-t border-background-200 pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-primary-500" aria-hidden="true" />
              {formatCount(topic.heat)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line" aria-hidden="true" />
              {formatCount(topic.comments)}
            </span>
          </span>
          <span className="whitespace-nowrap">{topic.updatedAt}</span>
        </div>
      </div>
    </article>
  );
}